/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { resourceRoutes } from '@/route-paths';
import { getBasePath } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export interface BraintreeTokenizeResult {
    nonce: string;
    deviceData: string;
    cardType: string;
    lastFour: string;
    expirationMonth: string;
    expirationYear: string;
}

export interface BraintreeHostedFieldsHandle {
    tokenize: () => Promise<BraintreeTokenizeResult>;
    isReady: boolean;
}

const HOSTED_FIELD_STYLE = {
    input: {
        'font-size': '16px',
        'font-family': 'inherit',
        color: 'inherit',
    },
    ':focus': { color: 'inherit' },
    'input.invalid': { color: 'hsl(var(--destructive))' },
    'input.valid': { color: 'inherit' },
};

const HOSTED_FIELD_CLASS =
    'w-full border border-input bg-background px-3 h-9 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

export const BraintreeHostedFields = forwardRef<BraintreeHostedFieldsHandle, { autoFocus?: boolean }>(
    function BraintreeHostedFields({ autoFocus = false }, ref) {
        const { t } = useTranslation('checkout');
        const [clientToken, setClientToken] = useState<string | null>(null);
        const serviceRef = useRef<unknown>(null);
        const hostedFieldsRef = useRef<unknown>(null);
        const dataCollectorRef = useRef<unknown>(null);
        const [isReady, setIsReady] = useState(false);
        const [error, setError] = useState<string | null>(null);

        // Use a direct fetch rather than useFetcher — React Router's fetcher prepends the
        // locale basename to the path, producing /multiverse-marketplace/en-US/resource/...
        // which the storefront proxy routes to MRT AFTER stripping the site prefix, leaving
        // /en-US/resource/... with no matching route. A plain fetch with getBasePath() uses
        // the same URL construction as other working resource routes (e.g. recommendations).
        useEffect(() => {
            let cancelled = false;
            const url = `${getBasePath()}${resourceRoutes.braintreeClientToken}`;
            fetch(url, { credentials: 'same-origin' })
                .then((r) => r.json())
                .then((data: { clientToken?: string; error?: string }) => {
                    if (cancelled) return;
                    if (data.clientToken) {
                        setClientToken(data.clientToken);
                    } else {
                        setError(data.error ?? 'Unable to initialize payment');
                    }
                })
                .catch(() => {
                    if (!cancelled) setError('Unable to initialize payment');
                });
            return () => {
                cancelled = true;
            };
        }, []);

        useEffect(() => {
            if (!clientToken) return;
            let cancelled = false;

            async function init() {
                try {
                    // Dynamically import so the heavy BT SDK is only loaded when needed
                    const btWeb = await import('braintree-web');
                    if (cancelled) return;

                    const clientInstance = await btWeb.client.create({
                        authorization: clientToken!,
                    });
                    if (cancelled) return;

                    serviceRef.current = clientInstance;

                    try {
                        dataCollectorRef.current = await btWeb.dataCollector.create({ client: clientInstance });
                    } catch {
                        // device data is optional — fraud detection enhancement only
                    }
                    if (cancelled) return;

                    const hf = await btWeb.hostedFields.create({
                        client: clientInstance,
                        styles: HOSTED_FIELD_STYLE,
                        fields: {
                            number: { selector: '#bt-card-number', placeholder: t('payment.cardNumberPlaceholder') },
                            cardholderName: {
                                selector: '#bt-cardholder-name',
                                placeholder: t('payment.cardholderPlaceholder'),
                            },
                            expirationDate: {
                                selector: '#bt-expiration-date',
                                placeholder: t('payment.expiryPlaceholder'),
                            },
                            cvv: { selector: '#bt-cvv', placeholder: t('payment.cvvPlaceholder') },
                        },
                    });
                    if (cancelled) return;

                    hostedFieldsRef.current = hf;
                    setIsReady(true);
                    if (autoFocus) {
                        (hf as { focus: (field: string) => void }).focus('cardholderName');
                    }
                } catch (err) {
                    if (!cancelled) {
                        setError(err instanceof Error ? err.message : 'Payment initialization failed');
                    }
                }
            }

            void init();
            return () => {
                cancelled = true;
            };
        }, [clientToken, autoFocus, t]);

        // Cleanup on unmount
        useEffect(() => {
            return () => {
                if (hostedFieldsRef.current) {
                    (hostedFieldsRef.current as { teardown: () => void }).teardown();
                }
                if (dataCollectorRef.current) {
                    (dataCollectorRef.current as { teardown: () => void }).teardown();
                }
                if (serviceRef.current) {
                    (serviceRef.current as { teardown: () => void }).teardown();
                }
            };
        }, []);

        useImperativeHandle(ref, () => ({
            isReady,
            async tokenize() {
                if (!hostedFieldsRef.current) {
                    throw new Error('Braintree Hosted Fields not initialized');
                }
                const hf = hostedFieldsRef.current as {
                    tokenize: () => Promise<{
                        nonce: string;
                        details: {
                            cardType: string;
                            lastFour: string;
                            expirationMonth: string;
                            expirationYear: string;
                        };
                    }>;
                };

                const result = await hf.tokenize();
                const dc = dataCollectorRef.current as { deviceData?: string } | null;

                return {
                    nonce: result.nonce,
                    deviceData: dc?.deviceData ?? '',
                    cardType: result.details.cardType,
                    lastFour: result.details.lastFour,
                    expirationMonth: result.details.expirationMonth,
                    expirationYear: result.details.expirationYear,
                };
            },
        }));

        if (error) {
            return (
                <p className="text-sm text-destructive" role="alert">
                    {error}
                </p>
            );
        }

        return (
            <div className="space-y-2">
                <div>
                    <label className="sr-only">{t('payment.nameOnCardLabel')}</label>
                    <div id="bt-cardholder-name" className={HOSTED_FIELD_CLASS} style={{ height: '36px' }} />
                </div>
                <div>
                    <label className="sr-only">{t('payment.cardNumberLabel')}</label>
                    <div id="bt-card-number" className={HOSTED_FIELD_CLASS} style={{ height: '36px' }} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="sr-only">{t('payment.expiryLabel')}</label>
                        <div id="bt-expiration-date" className={HOSTED_FIELD_CLASS} style={{ height: '36px' }} />
                    </div>
                    <div>
                        <label className="sr-only">{t('payment.cvvLabel')}</label>
                        <div id="bt-cvv" className={HOSTED_FIELD_CLASS} style={{ height: '36px' }} />
                    </div>
                </div>
                {!isReady && (
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    <p className="text-xs text-muted-foreground animate-pulse">{(t as any)('payment.loadingPaymentFields')}</p>
                )}
            </div>
        );
    }
);
